import React from 'react';
import type { ThemeClasses } from '@/ui/theme/ThemeContext';
import { Maximize2, MessageSquare, Minimize2, Send } from 'lucide-react';
import type { AgentProfile } from '@/features/projects/hooks/useAssigneeDirectory';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { AgentGlyph } from '@/shared/components/AgentGlyph';
import { cn } from '@/ui/theme/ThemeContext';
import { safeTaskChatArtifactUrl, taskChatService, type TaskChatMessage } from '@/features/tasks/services/taskChatService';
import { useAuth } from '@/features/auth/AuthContext';
import { authService } from '@/features/auth/services/authService';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { parseServerDateTime } from '@/shared/utils/serverTime';

const POLL_INTERVAL_MS = 5000;
const REQUEST_TIMEOUT_MS = 15000;
const liveOwner = () => JSON.stringify([
  authService.getSessionIdentity(), authService.getUser()?.id, getAdminViewAsUserId(),
]);

interface TaskChatSectionProps {
  projectId: string;
  taskId: string;
  assignedAgent?: AgentProfile;
  readOnly?: boolean;
  theme: ThemeClasses;
}

const visibleMessages = (messages: TaskChatMessage[], expanded: boolean): TaskChatMessage[] =>
  expanded ? messages : messages.slice(-3);

interface ContextMessages {
  contextKey: string;
  messages: TaskChatMessage[];
}

interface ContextStatus<T> {
  contextKey: string;
  value: T;
}

const reconcileMessages = (
  baseline: TaskChatMessage[], current: TaskChatMessage[], incoming: TaskChatMessage[],
): TaskChatMessage[] => {
  const baselineIds = new Set(baseline.map(message => message.id));
  const merged = new Map(incoming.map(message => [message.id, message]));
  // The response replaces its request baseline, while preserving messages
  // learned after that request started. This also respects server retention.
  current.filter(message => !baselineIds.has(message.id)).forEach(message => merged.set(message.id, message));
  return [...merged.values()].sort((left, right) =>
    (Date.parse(left.createdAt) || 0) - (Date.parse(right.createdAt) || 0));
};

export const TaskChatSection: React.FC<TaskChatSectionProps> = ({
  projectId,
  taskId,
  assignedAgent,
  readOnly = false,
  theme,
}) => {
  const { locale, t } = useSettings();
  const { user, actualUser, viewAsUserId, isAuthenticated } = useAuth();
  const owner = liveOwner();
  const ownerMatches = isAuthenticated && Boolean(user)
    && (actualUser?.id || user?.id) === authService.getUser()?.id
    && (viewAsUserId || null) === getAdminViewAsUserId();
  const contextKey = JSON.stringify([owner, user?.id, projectId, taskId]);
  const activeContextRef = React.useRef(contextKey);
  activeContextRef.current = contextKey;
  const activeOwnerRef = React.useRef(owner);
  activeOwnerRef.current = owner;
  const mountedRef = React.useRef(false);
  const pendingSendsRef = React.useRef(new Set<string>());
  const translateRef = React.useRef(t);
  translateRef.current = t;

  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const [messageState, setMessageState] = React.useState<ContextMessages>({ contextKey, messages: [] });
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [expanded, setExpanded] = React.useState(false);
  const [loadingState, setLoadingState] = React.useState<ContextStatus<boolean>>({ contextKey, value: false });
  const [sendingByContext, setSendingByContext] = React.useState<Record<string, boolean>>({});
  const [errorState, setErrorState] = React.useState<ContextStatus<string | null>>({ contextKey, value: null });

  const messages = messageState.contextKey === contextKey ? messageState.messages : [];
  const draft = drafts[contextKey] || '';
  const isLoading = loadingState.contextKey === contextKey && loadingState.value;
  const isSending = Boolean(sendingByContext[contextKey]);
  const error = errorState.contextKey === contextKey ? errorState.value : null;
  const messageStateRef = React.useRef(messageState);
  messageStateRef.current = messageState;

  React.useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let requestTimer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    const requestContext = contextKey;
    const requestOwner = owner;
    const isCurrent = () => !cancelled && ownerMatches
      && activeContextRef.current === requestContext && liveOwner() === requestOwner;
    const isVisible = () => document.visibilityState !== 'hidden';
    setMessageState(current => current.contextKey === requestContext
      ? current : { contextKey: requestContext, messages: [] });
    setErrorState({ contextKey: requestContext, value: null });

    const refresh = async () => {
      clearTimeout(pollTimer);
      if (!isCurrent() || !isVisible() || inFlight) return;
      inFlight = true;
      const baseline = messageStateRef.current.contextKey === requestContext ? messageStateRef.current.messages : [];
      setLoadingState({ contextKey: requestContext, value: baseline.length === 0 });
      controller = new AbortController();
      requestTimer = setTimeout(() => controller?.abort(), REQUEST_TIMEOUT_MS);
      try {
        const nextMessages = await taskChatService.list(projectId, taskId, controller.signal);
        if (isCurrent()) {
          setMessageState(current => ({ contextKey: requestContext, messages: reconcileMessages(
            baseline, current.contextKey === requestContext ? current.messages : [], nextMessages,
          ) }));
          setErrorState(current => current.contextKey === requestContext
            && current.value === translateRef.current('Failed to load task chat')
            ? { contextKey: requestContext, value: null } : current);
        }
      } catch {
        if (isCurrent() && isVisible()) {
          setErrorState({ contextKey: requestContext, value: translateRef.current('Failed to load task chat') });
        }
      } finally {
        inFlight = false;
        clearTimeout(requestTimer);
        if (isCurrent()) {
          setLoadingState({ contextKey: requestContext, value: false });
          if (isVisible()) pollTimer = setTimeout(() => void refresh(), POLL_INTERVAL_MS);
        }
      }
    };
    const onVisibilityChange = () => {
      clearTimeout(pollTimer);
      if (isVisible()) void refresh();
      else controller?.abort();
    };
    const onFocus = () => { void refresh(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    void refresh();

    return () => {
      cancelled = true;
      clearTimeout(pollTimer);
      clearTimeout(requestTimer);
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [contextKey, owner, ownerMatches, projectId, taskId]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || isSending || readOnly || !ownerMatches || liveOwner() !== owner
      || pendingSendsRef.current.has(contextKey)) {
      return;
    }

    const requestContext = contextKey;
    const requestOwner = owner;
    const isOwned = () => mountedRef.current && activeOwnerRef.current === requestOwner
      && liveOwner() === requestOwner;
    const submittedDraft = draft;
    const baseline = messages;
    pendingSendsRef.current.add(requestContext);
    setSendingByContext((current) => ({ ...current, [requestContext]: true }));
    setErrorState({ contextKey: requestContext, value: null });
    try {
      const nextMessages = await taskChatService.create(projectId, taskId, body);
      if (!isOwned()) return;
      if (activeContextRef.current === requestContext) {
        setMessageState(current => ({ contextKey: requestContext, messages: reconcileMessages(
          baseline, current.contextKey === requestContext ? current.messages : [], nextMessages,
        ) }));
        setLoadingState({ contextKey: requestContext, value: false });
      }
      setDrafts((current) => (
        current[requestContext] === submittedDraft
          ? { ...current, [requestContext]: '' }
          : current
      ));
    } catch {
      if (isOwned() && activeContextRef.current === requestContext) {
        setErrorState({ contextKey: requestContext, value: t('Failed to send task chat message') });
      }
    } finally {
      pendingSendsRef.current.delete(requestContext);
      if (isOwned()) setSendingByContext((current) => ({ ...current, [requestContext]: false }));
    }
  };

  const renderedMessages = visibleMessages(messages, expanded);
  const agentDisplayName = assignedAgent?.name === 'Agent'
    ? t('Agent')
    : assignedAgent?.name;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label className={cn('flex items-center gap-2 text-sm font-medium', theme.textSecondary)}>
          <MessageSquare size={16} />
          {t('Task chat')}
        </label>
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className={cn('inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs', theme.surfaceHover, theme.textSecondary)}
          aria-label={expanded ? t('Collapse task chat') : t('Open full task chat')}
        >
          {expanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          {expanded ? t('Mini chat') : t('Full chat')}
        </button>
      </div>

      <div className={cn('rounded-lg border p-3', theme.bgSecondary, theme.borderPrimary)}>
        {assignedAgent ? (
          <div className={cn('mb-3 rounded-md border px-3 py-2', theme.bgTertiary, theme.borderPrimary)}>
            <div className="flex items-center gap-2">
              <AgentGlyph size={15} className="text-sky-600 dark:text-sky-300" />
              <span className={cn('text-sm font-medium', theme.textPrimary)}>{agentDisplayName}</span>
              <span className={cn('rounded px-1.5 py-0.5 text-[10px] uppercase', theme.textMuted, theme.bgSecondary)}>
                {t('Suggest-only')}
              </span>
            </div>
            {assignedAgent.jobDescription ? (
              <p className={cn('mt-1 text-xs leading-relaxed', theme.textSecondary)}>
                {assignedAgent.jobDescription}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className={cn(expanded ? 'max-h-72' : 'max-h-32', 'space-y-2 overflow-y-auto pr-1')}>
          {isLoading ? (
            <div className={cn('text-sm', theme.textMuted)}>{t('Loading task chat')}</div>
          ) : renderedMessages.length > 0 ? (
            renderedMessages.map((message) => {
              const artifactUrl = safeTaskChatArtifactUrl(message.artifactUrl);
              return (
              <div key={message.id} className={cn('rounded-md px-2.5 py-2', theme.bgTertiary)}>
                <div className="flex items-center justify-between gap-2">
                  <span className={cn('truncate text-xs font-medium', theme.textPrimary)}>
                    {message.authorLabel || t('Unknown user')}
                  </span>
                  <span className={cn('flex-shrink-0 text-[10px]', theme.textMuted)}>
                    {parseServerDateTime(message.createdAt).toLocaleString(locale)}
                  </span>
                </div>
                <div className={cn('mt-1 whitespace-pre-wrap text-sm leading-relaxed', theme.textSecondary)}>
                  {message.body}
                </div>
                {artifactUrl ? (
                  <a href={artifactUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-sm underline">
                    {t('Artifacts')}
                  </a>
                ) : null}
              </div>
              );
            })
          ) : (
            <div className={cn('text-sm', theme.textMuted)}>
              {t('No task chat yet')}
            </div>
          )}
        </div>

        {error ? (
          <div className="mt-2 text-xs text-red-600 dark:text-red-300">{error}</div>
        ) : null}

        {!readOnly ? (
          <div className="mt-3 flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(event) => {
                const nextDraft = event.target.value;
                setDrafts((current) => ({ ...current, [contextKey]: nextDraft }));
              }}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  void handleSend();
                }
              }}
              rows={expanded ? 3 : 2}
              maxLength={4000}
              placeholder={t('Write a task message')}
              className={cn(
                'min-h-[40px] flex-1 resize-none rounded-md border px-3 py-2 text-sm outline-none',
                theme.bgPrimary,
                theme.borderPrimary,
                theme.textPrimary,
                theme.focusRing,
              )}
            />
            <button
              type="button"
              aria-label={t('Send message')}
              onClick={() => void handleSend()}
              disabled={!draft.trim() || isSending}
              className={cn(
                'inline-flex h-10 items-center justify-center rounded-md px-3 text-sm font-medium',
                theme.controlActive,
                (!draft.trim() || isSending) && 'cursor-not-allowed opacity-60',
              )}
            >
              <Send size={15} />
            </button>
          </div>
        ) : (
          <div className={cn('mt-2 text-xs', theme.textMuted)}>
            {t('Task chat is read-only for this account')}
          </div>
        )}
      </div>
    </section>
  );
};
